/**
 * Slice 9.5 — Audit log admin tab.
 *
 * Read-only browse of audit_event with action / target / since filters.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';

interface AuditEvent {
  id: string;
  actor_user_id: string | null;
  actor_email: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  request_method: string;
  request_path: string;
  status_code: number;
  ip: string | null;
  user_agent: string | null;
  detail_json: unknown;
  occurred_at: string;
}

const PAGE_SIZE = 50;

export function AuditAdminTab() {
  const [actionFilter, setActionFilter] = useState('');
  const [targetTypeFilter, setTargetTypeFilter] = useState('');
  const [sinceFilter, setSinceFilter] = useState('');
  const [offset, setOffset] = useState(0);

  const params = new URLSearchParams();
  params.set('limit', String(PAGE_SIZE));
  params.set('offset', String(offset));
  if (actionFilter) params.set('action', actionFilter);
  if (targetTypeFilter) params.set('target_type', targetTypeFilter);
  // sinceFilter is a datetime-local value (browser-local wall clock, no zone). Send it as a
  // UTC ISO instant so the server parses an unambiguous point in time instead of re-reading the
  // naive string in its own timezone (which shifted the filter boundary by the local offset).
  if (sinceFilter) {
    const d = new Date(sinceFilter);
    if (!Number.isNaN(d.getTime())) params.set('since', d.toISOString());
  }

  const auditQuery = useQuery<AuditEvent[]>({
    queryKey: ['admin-audit', actionFilter, targetTypeFilter, sinceFilter, offset],
    queryFn: () => api.get<AuditEvent[]>(`/admin/audit?${params.toString()}`),
  });

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <label className="text-xs text-ink-500">
          Action prefix
          <input
            type="text"
            placeholder="e.g. project. or auth.login"
            value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setOffset(0); }}
            className="block mt-1 px-2 py-1 border border-ink-200 rounded text-sm w-56"
          />
        </label>
        <label className="text-xs text-ink-500">
          Target type
          <input
            type="text"
            placeholder="e.g. project, user, asset"
            value={targetTypeFilter}
            onChange={(e) => { setTargetTypeFilter(e.target.value); setOffset(0); }}
            className="block mt-1 px-2 py-1 border border-ink-200 rounded text-sm w-44"
          />
        </label>
        <label className="text-xs text-ink-500">
          Since (ISO datetime)
          <input
            type="datetime-local"
            value={sinceFilter}
            onChange={(e) => { setSinceFilter(e.target.value); setOffset(0); }}
            className="block mt-1 px-2 py-1 border border-ink-200 rounded text-sm"
          />
        </label>
        {(actionFilter || targetTypeFilter || sinceFilter) && (
          <button
            type="button"
            onClick={() => { setActionFilter(''); setTargetTypeFilter(''); setSinceFilter(''); setOffset(0); }}
            className="text-xs text-accent hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {auditQuery.isLoading ? (
        <div className="p-6 text-ink-500 italic text-sm">Loading audit log…</div>
      ) : auditQuery.isError ? (
        <div className="p-6 text-red-700 text-sm">Failed to load audit log.</div>
      ) : (
        <>
          <div className="border border-ink-200 rounded overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-ink-50 text-ink-500 uppercase tracking-wide">
                <tr>
                  <th className="px-2 py-2 text-left">Time</th>
                  <th className="px-2 py-2 text-left">Actor</th>
                  <th className="px-2 py-2 text-left">Action</th>
                  <th className="px-2 py-2 text-left">Target</th>
                  <th className="px-2 py-2 text-left">Method</th>
                  <th className="px-2 py-2 text-left">Path</th>
                  <th className="px-2 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {(auditQuery.data ?? []).map((row) => (
                  <tr key={row.id} className="hover:bg-ink-50">
                    <td className="px-2 py-1 whitespace-nowrap font-mono">
                      {new Date(row.occurred_at).toLocaleString()}
                    </td>
                    <td className="px-2 py-1 font-mono">
                      {row.actor_email ?? <span className="text-ink-300">anonymous</span>}
                    </td>
                    <td className="px-2 py-1 font-mono">
                      {row.action}
                    </td>
                    <td className="px-2 py-1 font-mono">
                      {row.target_type ? (
                        <>
                          {row.target_type}
                          {row.target_id && (
                            <span className="text-ink-300 ml-1" title={row.target_id}>
                              ({row.target_id.slice(0, 8)}…)
                            </span>
                          )}
                        </>
                      ) : <span className="text-ink-300">—</span>}
                    </td>
                    <td className="px-2 py-1">{row.request_method}</td>
                    <td className="px-2 py-1 font-mono truncate max-w-xs" title={row.request_path}>
                      {row.request_path}
                    </td>
                    <td className="px-2 py-1">
                      <span className={statusColor(row.status_code)}>
                        {row.status_code}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center mt-3 text-xs text-ink-500">
            <span>
              Showing {offset + 1}–{offset + (auditQuery.data?.length ?? 0)}
            </span>
            <div className="space-x-2">
              <button
                type="button"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                className="px-2 py-1 border border-ink-200 rounded disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={(auditQuery.data?.length ?? 0) < PAGE_SIZE}
                onClick={() => setOffset(offset + PAGE_SIZE)}
                className="px-2 py-1 border border-ink-200 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function statusColor(code: number): string {
  if (code >= 500) return 'text-red-700 font-bold';
  if (code >= 400) return 'text-amber-700';
  if (code >= 300) return 'text-ink-500';
  return 'text-green-700';
}
